import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { Loader2, Clock, Link2Off, Box, CheckCheck, MessageSquare } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { decorate, infillsUsed } from "@/lib/glass";
import { withCouplings } from "@/lib/corners";
import { setCustomTypologies, typologyLabel } from "@/lib/customTypologies";
import { LOGO_LIGHT_SRC } from "@/lib/brand";
import { ElevationThumb } from "@/components/ElevationThumb";
import { ClientPanel, NameDialog } from "@/components/feedback/ClientPanel";
import { useFeedback, clientName, setClientName, clientKey, reviewTotals } from "@/lib/feedback";

const ThreeView = lazy(() => import("@/components/ThreeView"));
const HINT = "Drag or swipe to rotate · pinch or scroll to zoom · two fingers to pan · illustrative";

const Brand = ({ p }) => (p.hide_logo
  ? <span className="font-heading font-bold tracking-tight text-sm">{p.company || "Aluminium systems"}</span>
  : <img src={LOGO_LIGHT_SRC} alt="Artisan Guild Infra" className="h-7 sm:h-8 w-auto" data-testid="share-logo" />);

const DOT = { approved: "bg-emerald-400", changes: "bg-amber-400" };

const Picker = ({ items, activeId, onPick, summary }) => (
  <nav className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-y-auto px-3 py-3 lg:w-60 lg:border-r border-b lg:border-b-0 border-slate-800 bg-[#0B0F19] shrink-0" data-testid="share-item-picker">
    {items.map((it) => (
      <button key={it.id} onClick={() => onPick(it.id)} data-testid={`share-item-${it.tag}`}
        className={`shrink-0 flex items-center gap-2.5 rounded-md border px-2 py-1.5 text-left transition-colors ${it.id === activeId ? "border-cyan-500 bg-cyan-500/10" : "border-slate-800 hover:border-slate-600"}`}>
        <span className="w-9 h-9 shrink-0 p-0.5 relative"><ElevationThumb item={it} size={36} />
          <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#0B0F19] ${DOT[summary[it.id]?.status] || "bg-slate-600"}`} data-testid={`share-item-dot-${it.tag}`} /></span>
        <span className="min-w-0">
          <span className={`block font-mono text-xs font-semibold ${it.id === activeId ? "text-cyan-300" : "text-slate-200"}`}>{it.tag}</span>
          <span className="block font-mono text-[10px] text-slate-500 whitespace-nowrap">{it.width} × {it.height}{summary[it.id]?.count ? <> · <MessageSquare size={9} className="inline -mt-0.5" /> {summary[it.id].count}</> : null}</span>
        </span>
      </button>
    ))}
  </nav>
);

const InfillList = ({ used }) => (
    <span className="flex flex-col gap-1">
      {used.map((m) => <span key={m.spec} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-slate-500 shrink-0" style={{ background: m.print }} />{m.label} · {m.spec}</span>)}
    </span>
);

const Info = ({ item, expires, children }) => {
  const used = infillsUsed(item);
  const rows = [["Typology", typologyLabel(item.typology)], ["Size (W × H)", `${item.width} × ${item.height} mm`], ["Quantity", item.quantity || 1], ["Location", item.location],
    ["System", item.profile_system], ["Glass", item.glass_spec], ["Infill", used.length ? <InfillList used={used} /> : null], ["Finish", item.finish]].filter(([, v]) => v);
  return (
    <aside className="lg:w-80 shrink-0 border-t lg:border-t-0 lg:border-l border-slate-800 bg-[#0B0F19] p-4 space-y-4 lg:overflow-y-auto" data-testid="share-item-info">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-cyan-400">Item</p>
        <h2 className="font-heading text-xl font-semibold" data-testid="share-item-title">{item.tag} <span className="text-slate-400 text-base font-normal">{item.name || item.type}</span></h2>
      </div>
      <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-2 text-sm">
        {rows.map(([l, v]) => <div key={l} className="contents"><dt className="text-slate-500 text-xs pt-0.5">{l}</dt><dd className="text-slate-200" data-testid={`share-info-${l.split(" ")[0].toLowerCase()}`}>{v}</dd></div>)}
      </dl>
      {children}
      <p className="text-[11px] text-slate-500 border-t border-slate-800 pt-3" data-testid="share-expiry-note">View-only link{expires ? ` · valid until ${new Date(expires).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })}` : ""}. 3D is illustrative and not to scale.</p>
    </aside>
  );
};

const ApproveBar = ({ items, summary, onApproveAll }) => {
  const { approved } = reviewTotals(items, summary), left = items.length - approved;
  return (
    <div className="px-4 py-2 flex items-center gap-3 border-b border-slate-800 bg-[#0d1220]" data-testid="share-approve-bar">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-slate-400" data-testid="share-approved-count"><span className="text-emerald-300 font-semibold">{approved}</span> of {items.length} items approved</p>
        <div className="mt-1 h-1 rounded-full bg-slate-800 overflow-hidden max-w-xs"><div className="h-full bg-emerald-400 transition-all duration-500" style={{ width: `${(approved / (items.length || 1)) * 100}%` }} /></div>
      </div>
      <button onClick={onApproveAll} disabled={!left} data-testid="share-approve-all-btn"
        className="h-9 px-3 rounded-md border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10 text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-40 shrink-0">
        <CheckCheck size={14} />{left ? `Approve all${left < items.length ? ` ${left} left` : ""}` : "All approved"}
      </button>
    </div>
  );
};

const Message = ({ err }) => (
  <div className="min-h-[100dvh] bg-[#090D16] text-slate-100 grid place-items-center p-6" data-testid="share-error">
    <div className="max-w-sm text-center space-y-4">
      <img src={LOGO_LIGHT_SRC} alt="" className="h-8 mx-auto opacity-80" />
      {err.code === 410 ? <Clock className="mx-auto text-amber-400" size={36} /> : <Link2Off className="mx-auto text-rose-400" size={36} />}
      <p className="text-slate-300" data-testid="share-error-message">{err.msg}</p>
    </div>
  </div>
);

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    api.get(`/public/share/${token}`).then(({ data: d }) => { setCustomTypologies(d.typologies); setData(d); setActiveId(d.items[0]?.id); })
      .catch((e) => setErr({ code: e?.response?.status, msg: errMsg(e) }));
  }, [token]);
  const items = useMemo(() => (data ? withCouplings(data.items.map(decorate)) : []), [data]);
  const active = items.find((i) => i.id === activeId);
  const [fb, reloadFb] = useFeedback(data ? `/public/share/${token}/feedback` : null);
  const [nameAsk, setNameAsk] = useState(null);
  const [name, setName] = useState(clientName());
  const askName = () => new Promise((res) => setNameAsk({ ok: (n) => { setClientName(n); setName(n); setNameAsk(null); res(n); }, cancel: () => { setNameAsk(null); res(null); } }));
  const withName = async (fn) => { const n = name || (await askName()); return n ? fn(n) : false; };
  const fail = (e) => { toast.error(errMsg(e)); return false; };
  const post = (kind, text) => withName((n) => api.post(`/public/share/${token}/feedback`, { item_id: active.id, kind, text, author_name: n, client_key: clientKey() })
    .then(() => { reloadFb(); toast.success(kind === "approve" ? `${active.tag} approved — thank you!` : kind === "changes" ? "Change request sent" : "Comment posted"); }).catch(fail));
  const approveAll = () => withName((n) => {
    const left = items.length - reviewTotals(items, fb.summary).approved;
    if (!window.confirm(`Approve all ${left} remaining item${left === 1 ? "" : "s"} as ${n}?`)) return false;
    return api.post(`/public/share/${token}/approve-all`, { author_name: n, client_key: clientKey() }).then(({ data: r }) => { reloadFb(); toast.success(`${r.approved} item${r.approved === 1 ? "" : "s"} approved — thank you!`); }).catch(fail);
  });
  if (err) return <Message err={err} />;
  if (!data) return <div className="min-h-[100dvh] bg-[#090D16] grid place-items-center"><Loader2 className="animate-spin text-cyan-400" /></div>;
  const p = data.project;
  return (
    <div className="min-h-[100dvh] lg:h-[100dvh] flex flex-col bg-[#090D16] text-slate-100" data-testid="share-page">
      <header className="px-4 py-3 flex items-center gap-4 border-b border-slate-800 bg-[#0B0F19]">
        <Brand p={p} />
        <div className="min-w-0 ml-auto text-right">
          <h1 className="font-heading font-semibold text-sm sm:text-base truncate" data-testid="share-project-name">{p.name}</h1>
          <p className="text-[11px] text-slate-400 truncate" data-testid="share-client">{[p.client, p.site].filter(Boolean).join(" · ") || "3D preview"}</p>
        </div>
      </header>
      {active && <ApproveBar items={items} summary={fb.summary} onApproveAll={approveAll} />}
      {!active ? <div className="flex-1 grid place-items-center text-slate-500"><Box /></div> : (
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          <Picker items={items} activeId={activeId} onPick={setActiveId} summary={fb.summary} />
          <div className="h-[62vh] lg:h-auto lg:flex-1 flex flex-col min-w-0">
            <Suspense fallback={<div className="flex-1 grid place-items-center"><Loader2 className="animate-spin text-cyan-400" /></div>}>
              <ThreeView item={active} items={items} hint={HINT} />
            </Suspense>
          </div>
          <Info item={active} expires={data.expires_at}>
            <ClientPanel s={fb.summary[active.id]} entries={fb.entries.filter((e) => e.item_id === active.id)} onPost={post} name={name} onChangeName={() => askName()} />
          </Info>
        </div>
      )}
      {nameAsk && <NameDialog onSave={nameAsk.ok} onCancel={nameAsk.cancel} />}
    </div>
  );
}
